import React, { useState } from "react";
import { CalendarDays } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { fmtDate } from "@/lib/api";

const toDate = (s?: string) => {
  if (!s) return undefined;
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
};
const toStr = (d?: Date) => (d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}` : "");

interface Props {
  start: string;
  end: string;
  onChange: (start: string, end: string) => void;
  labels: [string, string];
  unavailable?: { start: string; end: string }[];
  testid: string;
  cellClass?: string;
}

export const DateRange = ({ start, end, onChange, labels, unavailable = [], testid, cellClass = "p-3" }: Props) => {
  const [open, setOpen] = useState(false);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const blocked = unavailable.map((u) => ({ from: toDate(u.start)!, to: new Date(toDate(u.end)!.getTime() - 86400000) }));
  const select = (r: any) => {
    const s = toStr(r?.from);
    const e = r?.to && toStr(r.to) !== s ? toStr(r.to) : "";
    onChange(s, e);
    if (s && e) setOpen(false);
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className="grid w-full grid-cols-2 text-left" data-testid={`${testid}-trigger`}>
          {[start, end].map((v, i) => (
            <span key={i} className={`${cellClass} ${i === 0 ? "border-r border-navy/10" : ""}`}>
              <span className="label">{labels[i]}</span>
              <span className={`flex items-center gap-1.5 whitespace-nowrap text-sm ${v ? "font-medium text-navy" : "text-navy/40"}`} data-testid={`${testid}-${i === 0 ? "start" : "end"}-value`}>
                <CalendarDays size={14} className="shrink-0 text-gold" /> {v ? fmtDate(v) : "Add date"}
              </span>
            </span>
          ))}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto rounded-2xl border-navy/10 p-2" align="start" data-testid={`${testid}-calendar`}>
        <Calendar mode="range" numberOfMonths={typeof window !== "undefined" && window.innerWidth < 640 ? 1 : 2}
          selected={{ from: toDate(start), to: toDate(end) }} onSelect={select} defaultMonth={toDate(start) || today}
          disabled={[{ before: today }, ...blocked]}
          classNames={{ day_selected: "bg-navy text-white hover:bg-navy hover:text-white focus:bg-navy focus:text-white", day_range_middle: "aria-selected:bg-gold-soft aria-selected:text-navy", day_today: "font-bold underline", day_disabled: "text-navy/25 line-through" }} />
        <div className="flex items-center justify-between px-3 pb-2 text-xs text-navy/55">
          <span>{start && !end ? "Select end date" : "Crossed-out dates are unavailable"}</span>
          {(start || end) && <button type="button" onClick={() => onChange("", "")} className="font-semibold underline" data-testid={`${testid}-clear`}>Clear</button>}
        </div>
      </PopoverContent>
    </Popover>
  );
};
